<template>
  <div class="company-object-ref-select">
    <template v-if="['edit'].includes(mode)">
      <template v-if="useNewDefinitionPicker">
        <div
          class="select-trigger-button"
          :class="{ 'is-disabled': isLoadingOptions }"
          role="button"
          @click="openSelectModal">
          <span class="selected-label">
            {{ vModel ? vModel.name : (defaultOption ? defaultOption.name : placeholder) }}
          </span>
          <i class="material-icons dropdown-icon">expand_more</i>
        </div>

        <div
          v-if="showSelectModal"
          class="definition-select-overlay"
          @click.self="showSelectModal = false"
          @keydown.escape="showSelectModal = false">
          <div
            class="definition-select"
            @click="showViewOptions = false">
            <!-- Header -->
            <div class="definition-select-header">
              <span class="header-title">Select a {{ placeholder }}</span>
              <button
                class="close-button"
                type="button"
                @click="showSelectModal = false">
                <i class="material-icons">close</i>
              </button>
            </div>

            <!-- Search bar -->
            <div class="search-bar">
              <div class="search-input-wrapper">
                <i class="material-icons search-icon">search</i>
                <input
                  ref="modalSearch"
                  v-model="searchQuery"
                  type="text"
                  class="search-input"
                  placeholder="Search"
                  @keydown.enter.prevent />
              </div>
              <div class="search-bar-actions">
                <div class="icon-button-wrapper">
                  <button
                    class="icon-button icon-button--green has-tooltip"
                    :data-tooltip="showViewOptions ? null : 'Options'"
                    type="button"
                    @click.stop="toggleViewOptions">
                    <i class="material-icons">tune</i>
                  </button>
                  <div
                    v-if="showViewOptions"
                    class="view-options-dropdown"
                    @click.stop>
                    <!-- Group the list by -->
                    <div class="vo-section">
                      <div
                        class="vo-section-header"
                        @click="voGroupOpen = !voGroupOpen">
                        <i class="material-icons vo-section-icon">list</i>
                        <span class="vo-section-title">Group the list by</span>
                        <i class="material-icons vo-chevron">
                          {{ voGroupOpen ? 'expand_less' : 'expand_more' }}
                        </i>
                      </div>
                      <div
                        v-if="voGroupOpen"
                        class="vo-section-body">
                        <div
                          v-for="opt in groupByOptions"
                          :key="opt.value"
                          class="vo-option"
                          :class="{ 'is-active': groupBy === opt.value }"
                          @click="groupBy = opt.value">
                          {{ opt.label }}
                        </div>
                      </div>
                    </div>
                    <!-- Archived -->
                    <div class="vo-section">
                      <div
                        class="vo-section-header"
                        @click="voArchivedOpen = !voArchivedOpen">
                        <i class="material-icons vo-section-icon">inventory_2</i>
                        <span class="vo-section-title">Archived {{ placeholder }}s</span>
                        <i class="material-icons vo-chevron">
                          {{ voArchivedOpen ? 'expand_less' : 'expand_more' }}
                        </i>
                      </div>
                      <div
                        v-if="voArchivedOpen"
                        class="vo-section-body">
                        <div
                          class="vo-option"
                          :class="{ 'is-active': archivedMode === 'hide' }"
                          @click="archivedMode = 'hide'">
                          Don't show
                        </div>
                        <div
                          class="vo-option"
                          :class="{ 'is-active': archivedMode === 'show' }"
                          @click="archivedMode = 'show'">
                          Show
                        </div>
                        <div
                          class="vo-option"
                          :class="{ 'is-active': archivedMode === 'only' }"
                          @click="archivedMode = 'only'">
                          Only show
                        </div>
                      </div>
                    </div>
                    <!-- Refresh -->
                    <div class="vo-section">
                      <div
                        class="vo-section-header vo-refresh"
                        @click="refreshOptions">
                        <i class="material-icons vo-section-icon">refresh</i>
                        <span class="vo-section-title">Refresh the list</span>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  class="icon-button icon-button--violet has-tooltip"
                  :class="{ 'is-active': showFilterPanel }"
                  :data-tooltip="showFilterPanel ? 'Hide filters' : 'Show filters'"
                  type="button"
                  @click="showFilterPanel = !showFilterPanel">
                  <i class="material-icons">filter_list</i>
                </button>
                <button
                  class="icon-button icon-button--orange has-tooltip has-tooltip--right"
                  :class="{ 'is-disabled': isDefaultState }"
                  :data-tooltip="isDefaultState ? null : 'Remove filters'"
                  type="button"
                  :disabled="isDefaultState"
                  @click="resetToDefaults">
                  <i class="material-icons">refresh</i>
                </button>
              </div>
            </div>

            <div class="definition-select-body">
              <!-- Items list -->
              <div
                class="items-list"
                :class="{ 'is-loading': isLoadingOptions }">
                <div
                  v-if="defaultOption"
                  class="item-row default-option-row"
                  :class="{ 'is-selected': !key }"
                  @click="selectDefaultOption">
                  <span class="item-name">{{ defaultOption.name }}</span>
                </div>
                <template v-for="groupData in groupedOptions">
                  <div
                    v-if="groupData.group"
                    :key="'group-' + groupData.group"
                    class="group-header"
                    @click="toggleGroupCollapse(groupData.group)">
                    <span class="group-header-label">{{ groupData.group }}</span>
                    <i class="material-icons group-chevron">
                      {{ collapsedGroups.includes(groupData.group) ? 'expand_more' : 'expand_less' }}
                    </i>
                  </div>
                  <template v-if="!groupData.group || !collapsedGroups.includes(groupData.group)">
                    <div
                      v-for="(option, index) in groupData.options"
                      :key="groupData.group + '-' + option.key + '-' + index"
                      class="item-row"
                      :class="{ 'is-selected': key === option.key }"
                      @click="selectOption(option)">
                      <span
                        class="item-type-icon"
                        :class="getItemIconClass(option)">
                        <i class="material-icons">{{ getItemIcon(option) }}</i>
                      </span>
                      <span
                        class="item-name"
                        :title="option.name">{{ option.name }}</span>
                      <span
                        v-if="option.is_archived"
                        class="item-archived-tag">
                        <i class="material-icons">inventory_2</i>
                      </span>
                    </div>
                  </template>
                </template>
                <div
                  v-if="filteredOptions.length === 0"
                  class="no-results">
                  No results found
                </div>
              </div>

              <!-- Filter panel -->
              <div
                v-if="showFilterPanel"
                class="filter-panel">
                <!-- Tag filters -->
                <div
                  v-for="category in tagCategories"
                  :key="category.id"
                  class="fp-section"
                  :class="{ 'is-loading': isLoadingTags }">
                  <div
                    class="fp-section-header"
                    @click.stop="toggleAccordion(category.id)">
                    <span
                      class="fp-label"
                      :class="{ 'is-filtered': getSelectedCount(category.id) > 0 }">
                      {{ category.displayName }}
                    </span>
                    <span
                      class="fp-badge"
                      :class="{ 'is-filtered': getSelectedCount(category.id) > 0 }">
                      {{ getSelectedCount(category.id) > 0 ? getSelectedCount(category.id) : 'All' }}
                    </span>
                    <i class="material-icons fp-chevron">
                      {{ openAccordions.includes(category.id) ? 'expand_less' : 'expand_more' }}
                    </i>
                  </div>
                  <div
                    v-if="openAccordions.includes(category.id) && !isLoadingTags"
                    class="fp-section-body">
                    <div class="fp-search-row">
                      <i class="material-icons fp-search-icon">search</i>
                      <input
                        v-model="filterSearchQueries[category.id]"
                        type="text"
                        class="fp-search-input"
                        placeholder="Search" />
                    </div>
                    <div class="fp-options">
                      <div class="fp-option">
                        <b-checkbox
                          :value="isAllSelected(category.id)"
                          @input="$event ? selectAllTags(category.id) : deselectAllTags(category.id)">
                          All
                        </b-checkbox>
                      </div>
                      <div
                        v-for="tagValue in getFilteredTagValues(category.id)"
                        :key="tagValue"
                        class="fp-option">
                        <b-checkbox
                          :value="(selectedTagFilters[category.id] || []).includes(tagValue)"
                          @input="toggleTagFilter(category.id, tagValue, $event)">
                          <span :title="formatTagValue(tagValue, category)">{{ formatTagValue(tagValue, category) }}</span>
                        </b-checkbox>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Functions -->
                <div
                  v-if="isMeasureType && availableFunctions.length > 0"
                  class="fp-section"
                  :class="{ 'is-loading': isLoadingOptions }">
                  <div
                    class="fp-section-header"
                    @click.stop="toggleAccordion('functions')">
                    <span
                      class="fp-label"
                      :class="{ 'is-filtered': selectedFunctionFilters.length > 0 && selectedFunctionFilters.length < availableFunctions.length }">
                      Functions
                    </span>
                    <span
                      class="fp-badge"
                      :class="{ 'is-filtered': selectedFunctionFilters.length > 0 && selectedFunctionFilters.length < availableFunctions.length }">
                      {{ selectedFunctionFilters.length > 0 && selectedFunctionFilters.length < availableFunctions.length ? selectedFunctionFilters.length : 'All' }}
                    </span>
                    <i class="material-icons fp-chevron">
                      {{ openAccordions.includes('functions') ? 'expand_less' : 'expand_more' }}
                    </i>
                  </div>
                  <div
                    v-if="openAccordions.includes('functions')"
                    class="fp-section-body">
                    <div class="fp-search-row">
                      <i class="material-icons fp-search-icon">search</i>
                      <input
                        v-model="filterSearchQueries['functions']"
                        type="text"
                        class="fp-search-input"
                        placeholder="Search" />
                    </div>
                    <div class="fp-options">
                      <div class="fp-option">
                        <b-checkbox
                          :value="selectedFunctionFilters.length === availableFunctions.length"
                          @input="$event ? selectAllFunctions() : deselectAllFunctions()">
                          All
                        </b-checkbox>
                      </div>
                      <div
                        v-for="fn in filteredFunctions"
                        :key="fn"
                        class="fp-option">
                        <b-checkbox
                          :value="selectedFunctionFilters.includes(fn)"
                          @input="toggleFunctionFilter(fn, $event)">
                          <span :title="formatFunctionName(fn)">{{ formatFunctionName(fn) }}</span>
                        </b-checkbox>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Dashboard Reports -->
                <div
                  class="fp-section"
                  :class="{ 'is-loading': isLoadingUsages }">
                  <div
                    class="fp-section-header"
                    @click.stop="!isLoadingUsages && toggleAccordion('dashboards')">
                    <span
                      class="fp-label"
                      :class="{ 'is-filtered': selectedDashboardFilters.length > 0 && selectedDashboardFilters.length < availableDashboardReferencers.length }">
                      Dashboard Reports
                    </span>
                    <span
                      class="fp-badge"
                      :class="{ 'is-filtered': selectedDashboardFilters.length > 0 && selectedDashboardFilters.length < availableDashboardReferencers.length }">
                      {{ selectedDashboardFilters.length > 0 && selectedDashboardFilters.length < availableDashboardReferencers.length ? selectedDashboardFilters.length : 'All' }}
                    </span>
                    <i class="material-icons fp-chevron">
                      {{ openAccordions.includes('dashboards') ? 'expand_less' : 'expand_more' }}
                    </i>
                  </div>
                  <div
                    v-if="openAccordions.includes('dashboards') && !isLoadingUsages"
                    class="fp-section-body">
                    <div class="fp-search-row">
                      <i class="material-icons fp-search-icon">search</i>
                      <input
                        v-model="filterSearchQueries['dashboards']"
                        type="text"
                        class="fp-search-input"
                        placeholder="Search" />
                    </div>
                    <div class="fp-options">
                      <div class="fp-option">
                        <b-checkbox
                          :value="selectedDashboardFilters.length === availableDashboardReferencers.length"
                          @input="$event ? selectAllDashboards() : deselectAllDashboards()">
                          All
                        </b-checkbox>
                      </div>
                      <div
                        v-for="referencer in filteredDashboardReferencers"
                        :key="referencer"
                        class="fp-option">
                        <b-checkbox
                          :value="selectedDashboardFilters.includes(referencer)"
                          @input="toggleDashboardFilter(referencer, $event)">
                          <span :title="formatReferencerName(referencer, 'Dashboards')">{{ formatReferencerName(referencer, 'Dashboards') }}</span>
                        </b-checkbox>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Email Reports -->
                <div
                  class="fp-section"
                  :class="{ 'is-loading': isLoadingUsages }">
                  <div
                    class="fp-section-header"
                    @click.stop="!isLoadingUsages && toggleAccordion('pdfReports')">
                    <span
                      class="fp-label"
                      :class="{ 'is-filtered': selectedPdfReportFilters.length > 0 && selectedPdfReportFilters.length < availablePdfReportReferencers.length }">
                      Email Reports
                    </span>
                    <span
                      class="fp-badge"
                      :class="{ 'is-filtered': selectedPdfReportFilters.length > 0 && selectedPdfReportFilters.length < availablePdfReportReferencers.length }">
                      {{ selectedPdfReportFilters.length > 0 && selectedPdfReportFilters.length < availablePdfReportReferencers.length ? selectedPdfReportFilters.length : 'All' }}
                    </span>
                    <i class="material-icons fp-chevron">
                      {{ openAccordions.includes('pdfReports') ? 'expand_less' : 'expand_more' }}
                    </i>
                  </div>
                  <div
                    v-if="openAccordions.includes('pdfReports') && !isLoadingUsages"
                    class="fp-section-body">
                    <div class="fp-search-row">
                      <i class="material-icons fp-search-icon">search</i>
                      <input
                        v-model="filterSearchQueries['pdfReports']"
                        type="text"
                        class="fp-search-input"
                        placeholder="Search" />
                    </div>
                    <div class="fp-options">
                      <div class="fp-option">
                        <b-checkbox
                          :value="selectedPdfReportFilters.length === availablePdfReportReferencers.length"
                          @input="$event ? selectAllPdfReports() : deselectAllPdfReports()">
                          All
                        </b-checkbox>
                      </div>
                      <div
                        v-for="referencer in filteredPdfReportReferencers"
                        :key="referencer"
                        class="fp-option">
                        <b-checkbox
                          :value="selectedPdfReportFilters.includes(referencer)"
                          @input="togglePdfReportFilter(referencer, $event)">
                          <span :title="formatReferencerName(referencer, 'Email Reports')">{{ formatReferencerName(referencer, 'Email Reports') }}</span>
                        </b-checkbox>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <button
          v-if="key"
          class="button action-button"
          @click="openSelected">
          <i class="material-icons">open_in_new</i>
        </button>
        <button
          v-if="key"
          class="button action-button"
          @click="showVisualizer = true">
          <i class="material-icons">visibility</i>
        </button>
      </template>
      <template v-else>
        <singleselect
          v-model="vModel"
          class="select-input"
          :loading="isLoadingOptions"
          :options="legacyOptions"
          label="name"
          track-by="key">
          <template #reload>
            <button
              class="button refresh-button-legacy"
              @click.prevent="refreshOptions">
              <i class="material-icons mr-2"> refresh </i>
              <span>Refresh Options</span>
            </button>
          </template>
        </singleselect>
        <button
          v-if="key"
          class="button open-selected"
          @click="openSelected">
          <i class="material-icons"> open_in_new </i>
        </button>
        <button
          v-if="key"
          class="button open-selected"
          @click="showVisualizer = true">
          <i class="material-icons"> visibility </i>
        </button>
      </template>
    </template>

    <template v-if="['summary'].includes(mode)">
      {{ vModel ? vModel.name : (defaultOption ? defaultOption.name : '') }}
    </template>

    <b-modal
      v-model="showVisualizer"
      :can-cancel="['x', 'escape']"
      has-modal-card
      trap-focus
      :destroy-on-hide="false"
      close-button-aria-label="Close"
      aria-modal>
      <form action="">
        <div class="modal-card select-module-modal">
          <div class="modal-body card">
            <visualize-object :data="vModel" />
          </div>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
  import { openInParent } from '@/utils/parentNav'
  import visualizeObject from '@/components/reports/config/common/CompanyObjectVisualizer.vue'
  import singleselect from '@/components/ui/singleselect.vue'
  import { mapGetters } from 'vuex'

  const TAG_NONE_VALUE = '__none__'
  const USAGE_NONE_VALUE = '__not_used__'

  const DASHBOARD_USAGE_TYPES = ['DASHBOARD_MODULE',]
  const PDF_REPORT_USAGE_TYPES = ['PDF_REPORT',]

  const OBJECT_TYPE_MAP = {
    company_measure: 'CompanyMeasureRef',
    company_dimension: 'CompanyDimensionRef',
    company_filter: 'CompanyFilterRef',
    company_expression: 'CompanyExpressionRef',
  }

  const MEASURE_FUNCTION_ICONS = {
    kpi: { icon: 'trending_up', cssClass: 'icon--green', },
    reporting: { icon: 'insert_chart', cssClass: 'icon--sky', },
    reference: { icon: 'call_made', cssClass: 'icon--orange', },
    calculation: { icon: 'calculate', cssClass: 'icon--red', },
  }

  const MEASURE_DEFAULT_ICON = { icon: 'insert_chart', cssClass: 'icon--gray', }

  const DEFINITION_TYPE_ICONS = {
    company_dimension: { icon: 'bar_chart', cssClass: 'icon--sky', },
    company_filter: { icon: 'filter_alt', cssClass: 'icon--sky', },
  }

  const DEFAULT_FUNCTION_FILTERS = ['reporting', 'kpi',]

  const TAG_CATEGORIES = {
    company_measure: [
      { id: 'measure_category', displayName: 'Category', allowMultiple: false, },
      { id: 'measure_topic', displayName: 'Topic', allowMultiple: false, },
      { id: 'measure_type', displayName: 'Type', allowMultiple: false, },
      { id: 'measure_custom', displayName: 'Custom Tags', allowMultiple: true, },
    ],
    company_dimension: [
      { id: 'dimension_category', displayName: 'Category', allowMultiple: false, },
      { id: 'dimension_custom', displayName: 'Custom', allowMultiple: true, },
    ],
    company_filter: [
      { id: 'filter_category', displayName: 'Category', allowMultiple: false, },
      { id: 'filter_custom', displayName: 'Custom', allowMultiple: true, },
    ],
  }

  export default {
    name: 'CompanyObjectRefSelect',
    components: {
      visualizeObject,
      singleselect,
    },
    props: {
      placeholder: {
        type: String,
        required: true,
      },
      objectType: {
        type: String,
        required: true,
      },
      externalRoute: {
        type: String,
        default: null,
      },
      externalPath: {
        type: String,
        default: null,
      },
      externalQuery: {
        type: Object,
        required: true,
      },
      options: {
        type: Object,
        required: true,
      },
      value: {
        type: Object,
        default: null,
      },
      defaultOption: {
        type: Object,
        default: null,
      },
      mode: {
        type: String,
        default: 'edit',
      },
    },
    data () {
      return {
        showVisualizer: false,
        showSelectModal: false,
        searchQuery: '',
        archivedMode: 'hide',
        openAccordions: [],
        selectedTagFilters: {},
        selectedFunctionFilters: [],
        selectedDashboardFilters: [],
        selectedPdfReportFilters: [],
        groupBy: null,
        showFilterPanel: false,
        showViewOptions: false,
        voGroupOpen: true,
        voArchivedOpen: true,
        collapsedGroups: [],
        filterSearchQueries: {},
      }
    },
    computed: {
      ...mapGetters({
        categoryTags: 'app/reportsConfig/categoryTags',
      }),
      useNewDefinitionPicker () {
        if (this.$route.query.use_legacy_definition_picker === 'true') return false
        return this.objectType !== 'company_expression'
      },
      isLoading () {
        return this.$store.getters['app/reportsConfig/loading']
      },
      isLoadingOptions () {
        const loadingOptions = this.$store.getters['app/reportsConfig/loadingOptions']
        return loadingOptions?.[this.objectType] ?? true
      },
      isLoadingTags () {
        const loadingTags = this.$store.getters['app/reportsConfig/loadingTags']
        return loadingTags?.[this.objectType] ?? true
      },
      isLoadingUsages () {
        const loadingUsages = this.$store.getters['app/reportsConfig/loadingUsages']
        return loadingUsages?.[this.objectType] ?? true
      },
      legacyOptions () {
        const opts = Object.values(this.options)
        if (this.defaultOption) {
          return [{ key: null, name: this.defaultOption.name, }, ...opts,]
        }
        return opts
      },
      vModel: {
        get () {
          if (this.defaultOption && !this.key) {
            return { key: null, name: this.defaultOption.name, }
          }
          return this.options[this.key]
        },
        set (vModel) {
          if (vModel && vModel.key === null) {
            this.$emit('input', null)
            return
          }
          this.$emit('input', {
            type: OBJECT_TYPE_MAP[this.objectType],
            name: vModel.name,
            key: vModel.key,
          })
        },
      },
      key () {
        return this.value?.key
      },
      bulkTags () {
        const tagGetterMap = {
          company_measure: 'app/reportsConfig/companyMeasureTags',
          company_dimension: 'app/reportsConfig/companyDimensionTags',
          company_filter: 'app/reportsConfig/companyFilterTags',
          company_expression: 'app/reportsConfig/companyExpressionTags',
        }
        const getter = tagGetterMap[this.objectType]
        const tags = getter ? this.$store.getters[getter] : null
        return Array.isArray(tags) ? tags : []
      },
      tagsByKey () {
        const map = {}
        for (const item of this.bulkTags) {
          map[item.key] = item.tags || {}
        }
        return map
      },
      tagCategories () {
        return TAG_CATEGORIES[this.objectType] || []
      },
      primaryCategory () {
        return this.tagCategories[0] || null
      },
      secondaryCategories () {
        return this.tagCategories.slice(1)
      },
      availableTagValues () {
        const result = {}
        const visibleKeys = this.archiveFilteredKeys
        for (const category of this.tagCategories) {
          const values = new Set()
          for (const item of this.bulkTags) {
            if (!visibleKeys.has(item.key)) continue
            const tagValue = item.tags?.[category.id]
            if (category.allowMultiple) {
              if (Array.isArray(tagValue)) {
                tagValue.forEach(v => values.add(v))
              }
            } else {
              if (tagValue) {
                values.add(tagValue)
              }
            }
          }
          result[category.id] = [TAG_NONE_VALUE, ...Array.from(values).sort(),]
        }
        return result
      },
      isMeasureType () {
        return this.objectType === 'company_measure'
      },
      defaultGroupBy () {
        return this.primaryCategory ? `tag:${this.primaryCategory.id}` : null
      },
      defaultFunctionFilters () {
        if (!this.isMeasureType) return []
        const defaults = this.availableFunctions.filter(fn => DEFAULT_FUNCTION_FILTERS.includes(fn))
        return defaults.length > 0 ? defaults : [...this.availableFunctions,]
      },
      isDefaultState () {
        if (this.groupBy !== this.defaultGroupBy) return false
        if (this.archivedMode !== 'hide') return false
        if (this.searchQuery !== '') return false
        if (!this.isLoadingTags) {
          for (const category of this.tagCategories) {
            const selected = this.selectedTagFilters[category.id] || []
            const all = this.availableTagValues[category.id] || []
            if (selected.length !== all.length) return false
          }
        }
        if (this.isMeasureType) {
          const currentFns = [...this.selectedFunctionFilters,].sort()
          const defaultFns = [...this.defaultFunctionFilters,].sort()
          if (currentFns.length !== defaultFns.length || currentFns.some((v, i) => v !== defaultFns[i])) return false
        }
        if (!this.isLoadingUsages) {
          if (this.selectedDashboardFilters.length !== this.availableDashboardReferencers.length) return false
          if (this.selectedPdfReportFilters.length !== this.availablePdfReportReferencers.length) return false
        }
        return true
      },
      availableFunctions () {
        if (!this.isMeasureType) return []
        const fns = new Set()
        for (const option of this.archiveFilteredOptions) {
          if (option.function) {
            fns.add(option.function)
          }
        }
        return Array.from(fns).sort()
      },
      bulkUsages () {
        const usageGetterMap = {
          company_measure: 'app/reportsConfig/companyMeasureUsages',
          company_dimension: 'app/reportsConfig/companyDimensionUsages',
          company_filter: 'app/reportsConfig/companyFilterUsages',
          company_expression: 'app/reportsConfig/companyExpressionUsages',
        }
        const getter = usageGetterMap[this.objectType]
        return getter ? this.$store.getters[getter] : {}
      },
      dashboardReferencersByKey () {
        const map = {}
        const usages = this.bulkUsages?.usages || []
        for (const usage of usages) {
          if (DASHBOARD_USAGE_TYPES.includes(usage.referencer_type)) {
            if (!map[usage.definition_key]) {
              map[usage.definition_key] = new Set()
            }
            map[usage.definition_key].add(usage.referencer_name)
          }
        }
        for (const key of Object.keys(map)) {
          map[key] = Array.from(map[key])
        }
        return map
      },
      availableDashboardReferencers () {
        const referencers = new Set()
        const visibleKeys = this.archiveFilteredKeys
        for (const [key, names,] of Object.entries(this.dashboardReferencersByKey)) {
          if (!visibleKeys.has(key)) continue
          for (const name of names) {
            referencers.add(name)
          }
        }
        return [USAGE_NONE_VALUE, ...Array.from(referencers).sort(),]
      },
      pdfReportReferencersByKey () {
        const map = {}
        const usages = this.bulkUsages?.usages || []
        for (const usage of usages) {
          if (PDF_REPORT_USAGE_TYPES.includes(usage.referencer_type)) {
            if (!map[usage.definition_key]) {
              map[usage.definition_key] = new Set()
            }
            map[usage.definition_key].add(usage.referencer_name)
          }
        }
        for (const key of Object.keys(map)) {
          map[key] = Array.from(map[key])
        }
        return map
      },
      availablePdfReportReferencers () {
        const referencers = new Set()
        const visibleKeys = this.archiveFilteredKeys
        for (const [key, names,] of Object.entries(this.pdfReportReferencersByKey)) {
          if (!visibleKeys.has(key)) continue
          for (const name of names) {
            referencers.add(name)
          }
        }
        return [USAGE_NONE_VALUE, ...Array.from(referencers).sort(),]
      },
      filteredFunctions () {
        const query = (this.filterSearchQueries['functions'] || '').toLowerCase()
        if (!query) return this.availableFunctions
        return this.availableFunctions.filter(fn =>
          this.formatFunctionName(fn).toLowerCase().includes(query)
        )
      },
      filteredDashboardReferencers () {
        const query = (this.filterSearchQueries['dashboards'] || '').toLowerCase()
        if (!query) return this.availableDashboardReferencers
        return this.availableDashboardReferencers.filter(r => {
          const display = this.formatReferencerName(r, 'Dashboards')
          return display.toLowerCase().includes(query)
        })
      },
      filteredPdfReportReferencers () {
        const query = (this.filterSearchQueries['pdfReports'] || '').toLowerCase()
        if (!query) return this.availablePdfReportReferencers
        return this.availablePdfReportReferencers.filter(r => {
          const display = this.formatReferencerName(r, 'Email Reports')
          return display.toLowerCase().includes(query)
        })
      },
      archiveFilteredOptions () {
        let options = Object.values(this.options)
        if (this.archivedMode === 'hide') {
          options = options.filter(option => !option.is_archived)
        } else if (this.archivedMode === 'only') {
          options = options.filter(option => option.is_archived)
        }
        return options
      },
      archiveFilteredKeys () {
        return new Set(this.archiveFilteredOptions.map(o => o.key))
      },
      filteredOptions () {
        let options = this.archiveFilteredOptions
        if (this.searchQuery) {
          const query = this.searchQuery.toLowerCase()
          options = options.filter(option =>
            option.name.toLowerCase().includes(query)
          )
        }
        if (!this.isLoadingTags) {
          for (const category of this.tagCategories) {
            const selectedValues = this.selectedTagFilters[category.id] || []

            options = options.filter(option => {
              const optionTags = this.tagsByKey[option.key] || {}
              const tagValue = optionTags[category.id]

              if (category.allowMultiple) {
                const hasNoValue = !Array.isArray(tagValue) || tagValue.length === 0
                if (selectedValues.includes(TAG_NONE_VALUE) && hasNoValue) {
                  return true
                }
                if (!Array.isArray(tagValue)) return false
                return selectedValues.some(v => v !== TAG_NONE_VALUE && tagValue.includes(v))
              } else {
                if (selectedValues.includes(TAG_NONE_VALUE) && !tagValue) {
                  return true
                }
                return selectedValues.includes(tagValue)
              }
            })
          }
        }
        if (this.isMeasureType && this.selectedFunctionFilters.length > 0 && this.selectedFunctionFilters.length < this.availableFunctions.length) {
          options = options.filter(option => this.selectedFunctionFilters.includes(option.function))
        }
        if (!this.isLoadingUsages) {
          if (this.selectedDashboardFilters.length === 0) {
            options = []
          } else {
            options = options.filter(option => {
              const referencers = this.dashboardReferencersByKey[option.key] || []
              const hasNoUsage = referencers.length === 0
              if (this.selectedDashboardFilters.includes(USAGE_NONE_VALUE) && hasNoUsage) {
                return true
              }
              return this.selectedDashboardFilters.some(
                name => name !== USAGE_NONE_VALUE && referencers.includes(name)
              )
            })
          }
        }
        if (!this.isLoadingUsages) {
          if (this.selectedPdfReportFilters.length === 0) {
            options = []
          } else {
            options = options.filter(option => {
              const referencers = this.pdfReportReferencersByKey[option.key] || []
              const hasNoUsage = referencers.length === 0
              if (this.selectedPdfReportFilters.includes(USAGE_NONE_VALUE) && hasNoUsage) {
                return true
              }
              return this.selectedPdfReportFilters.some(
                name => name !== USAGE_NONE_VALUE && referencers.includes(name)
              )
            })
          }
        }
        return options
      },
      groupByOptions () {
        const options = [
          { value: null, label: 'None', },
        ]
        for (const category of this.tagCategories) {
          options.push({
            value: `tag:${category.id}`,
            label: category.displayName,
          })
        }
        if (this.isMeasureType) {
          options.push({
            value: 'function',
            label: 'Functions',
          })
        }
        options.push({
          value: 'dashboards',
          label: 'Dashboard Reports',
        })
        options.push({
          value: 'pdfReports',
          label: 'Email Reports',
        })
        return options
      },
      groupedOptions () {
        if (!this.groupBy || this.isLoadingTags || this.isLoadingUsages) {
          return [{ group: null, options: this.filteredOptions, },]
        }

        const groups = {}
        const noGroupLabel = 'Ungrouped'

        for (const option of this.filteredOptions) {
          let values = []

          if (this.groupBy.startsWith('tag:')) {
            const categoryId = this.groupBy.replace('tag:', '')
            const optionTags = this.tagsByKey[option.key] || {}
            const tagValue = optionTags[categoryId]
            const category = this.tagCategories.find(c => c.id === categoryId)

            if (category?.allowMultiple && Array.isArray(tagValue)) {
              values = tagValue.length > 0 ? tagValue.map(tagItem => this.formatTagValue(tagItem, category)) : [noGroupLabel,]
            } else if (tagValue, category) {
              values = [this.formatTagValue(tagValue, category),]
            } else {
              values = [noGroupLabel,]
            }
          } else if (this.groupBy === 'function') {
            values = option.function ? [this.formatFunctionName(option.function),] : [noGroupLabel,]
          } else if (this.groupBy === 'dashboards') {
            const referencers = this.dashboardReferencersByKey[option.key] || []
            values = referencers.length > 0 ? referencers : [noGroupLabel,]
          } else if (this.groupBy === 'pdfReports') {
            const referencers = this.pdfReportReferencersByKey[option.key] || []
            values = referencers.length > 0 ? referencers : [noGroupLabel,]
          }

          for (const value of values) {
            if (!groups[value]) {
              groups[value] = []
            }
            groups[value].push(option)
          }
        }

        const sortedKeys = Object.keys(groups).sort((a, b) => {
          if (a === noGroupLabel) return 1
          if (b === noGroupLabel) return -1
          return a.localeCompare(b)
        })

        return sortedKeys.map(groupKey => ({
          group: groupKey,
          options: groups[groupKey],
        }))
      },
    },
    watch: {
      isLoadingTags (loading, wasLoading) {
        if (wasLoading && !loading) {
          this.initializeTagFilters()
        }
      },
      isLoadingUsages (loading, wasLoading) {
        if (wasLoading && !loading) {
          this.initializeUsageFilters()
        }
      },
      isLoadingOptions (loading, wasLoading) {
        if (wasLoading && !loading) {
          this.initializeFunctionFilters()
        }
      },
      showSelectModal (val) {
        if (val) {
          document.documentElement.classList.add('is-clipped')
        } else {
          document.documentElement.classList.remove('is-clipped')
          this.showViewOptions = false
        }
      },
    },
    beforeDestroy () {
      document.documentElement.classList.remove('is-clipped')
    },
    mounted () {
      if (!this.isLoadingTags) {
        this.initializeTagFilters()
      }
      if (!this.isLoadingUsages) {
        this.initializeUsageFilters()
      }
      this.initializeFunctionFilters()
      if (this.primaryCategory) {
        this.groupBy = this.defaultGroupBy
        if (!this.openAccordions.includes(this.primaryCategory.id)) {
          this.openAccordions.push(this.primaryCategory.id)
        }
      }
    },
    methods: {
      openSelectModal () {
        if (!this.isLoadingOptions) {
          this.showSelectModal = true
          this.$nextTick(() => {
            this.$nextTick(() => {
              this.$refs.modalSearch.focus()
            })
          })
        }
      },
      toggleAccordion (accordionId) {
        if (this.openAccordions.includes(accordionId)) {
          this.openAccordions = this.openAccordions.filter(a => a !== accordionId)
        } else {
          this.openAccordions.push(accordionId)
        }
      },
      toggleViewOptions () {
        this.showViewOptions = !this.showViewOptions
      },
      toggleGroupCollapse (groupName) {
        if (this.collapsedGroups.includes(groupName)) {
          this.collapsedGroups = this.collapsedGroups.filter(g => g !== groupName)
        } else {
          this.collapsedGroups.push(groupName)
        }
      },
      isAllSelected (categoryId) {
        const selected = this.selectedTagFilters[categoryId] || []
        const total = this.availableTagValues[categoryId]?.length || 0
        return selected.length === total
      },
      getSelectedCount (categoryId) {
        const selected = this.selectedTagFilters[categoryId] || []
        const total = this.availableTagValues[categoryId]?.length || 0
        if (selected.length === total) {
          return 0
        }
        return selected.length
      },
      getFilteredTagValues (categoryId) {
        const all = this.availableTagValues[categoryId] || []
        const query = (this.filterSearchQueries[categoryId] || '').toLowerCase()
        if (!query) return all
        return all.filter(v => {
          const category = this.tagCategories.find(c => c.id === categoryId)
          const display = this.formatTagValue(v, category) || ''
          return display.toLowerCase().includes(query)
        })
      },
      getItemIcon (option) {
        if (this.objectType === 'company_measure') {
          const fnIcon = option.function && MEASURE_FUNCTION_ICONS[option.function]
          return fnIcon ? fnIcon.icon : MEASURE_DEFAULT_ICON.icon
        }
        const typeIcon = DEFINITION_TYPE_ICONS[this.objectType]
        return typeIcon ? typeIcon.icon : 'insert_chart'
      },
      getItemIconClass (option) {
        if (this.objectType === 'company_measure') {
          const fnIcon = option.function && MEASURE_FUNCTION_ICONS[option.function]
          return fnIcon ? fnIcon.cssClass : MEASURE_DEFAULT_ICON.cssClass
        }
        const typeIcon = DEFINITION_TYPE_ICONS[this.objectType]
        return typeIcon ? typeIcon.cssClass : 'icon--sky'
      },
      initializeFunctionFilters () {
        if (!this.isMeasureType) return
        this.selectedFunctionFilters = [...this.defaultFunctionFilters,]
      },
      resetToDefaults () {
        this.groupBy = this.defaultGroupBy
        this.archivedMode = 'hide'
        this.searchQuery = ''
        this.filterSearchQueries = {}
        this.collapsedGroups = []
        if (!this.isLoadingTags) {
          this.initializeTagFilters()
        }
        this.initializeFunctionFilters()
        if (!this.isLoadingUsages) {
          this.initializeUsageFilters()
        }
      },
      refreshOptions () {
        this.resetToDefaults()
        this.showViewOptions = false
        this.$store.dispatch('app/reportsConfig/refreshOptions')
      },
      openSelected () {
        let href
        if (this.externalPath) {
          const companyId = this.$route.params.company_id
          const query = new URLSearchParams({
            ...this.externalQuery,
            key: this.key,
          }).toString()
          href = `/company/${companyId}${this.externalPath}?${query}`
        } else {
          const routeData = this.$router.resolve({
            name: this.externalRoute,
            query: {
              ...this.externalQuery,
              key: this.key,
            },
          })
          href = routeData.href
        }
        openInParent(href)
      },
      selectDefaultOption () {
        this.$emit('input', null)
        this.showSelectModal = false
        this.searchQuery = ''
      },
      selectOption (option) {
        this.$emit('input', {
          type: OBJECT_TYPE_MAP[this.objectType],
          name: option.name,
          key: option.key,
        })
        this.showSelectModal = false
        this.searchQuery = ''
      },
      toggleTagFilter (categoryId, tagValue, isSelected) {
        const current = this.selectedTagFilters[categoryId] || []
        if (isSelected) {
          this.$set(this.selectedTagFilters, categoryId, [...current, tagValue,])
        } else {
          this.$set(
            this.selectedTagFilters,
            categoryId,
            current.filter(v => v !== tagValue)
          )
        }
      },
      formatTagValue (value, category) {
        if (this.isLoadingTags) {
          return 'Loading'
        }
        return value === TAG_NONE_VALUE ? `No ${category.displayName}` : ((this.categoryTags[category.id] || {})[value] || null)?.display_name
      },
      selectAllTags (categoryId) {
        this.$set(this.selectedTagFilters, categoryId, [...this.availableTagValues[categoryId],])
      },
      deselectAllTags (categoryId) {
        this.$set(this.selectedTagFilters, categoryId, [])
      },
      initializeTagFilters () {
        for (const category of this.tagCategories) {
          this.$set(this.selectedTagFilters, category.id, [...(this.availableTagValues[category.id] || []),])
        }
      },
      initializeUsageFilters () {
        this.selectedDashboardFilters = [...this.availableDashboardReferencers,]
        this.selectedPdfReportFilters = [...this.availablePdfReportReferencers,]
      },
      toggleDashboardFilter (referencer, isSelected) {
        if (isSelected) {
          this.selectedDashboardFilters = [...this.selectedDashboardFilters, referencer,]
        } else {
          this.selectedDashboardFilters = this.selectedDashboardFilters.filter(r => r !== referencer)
        }
      },
      togglePdfReportFilter (referencer, isSelected) {
        if (isSelected) {
          this.selectedPdfReportFilters = [...this.selectedPdfReportFilters, referencer,]
        } else {
          this.selectedPdfReportFilters = this.selectedPdfReportFilters.filter(r => r !== referencer)
        }
      },
      toggleFunctionFilter (fn, isSelected) {
        if (isSelected) {
          this.selectedFunctionFilters = [...this.selectedFunctionFilters, fn,]
        } else {
          this.selectedFunctionFilters = this.selectedFunctionFilters.filter(f => f !== fn)
        }
      },
      selectAllFunctions () {
        this.selectedFunctionFilters = [...this.availableFunctions,]
      },
      deselectAllFunctions () {
        this.selectedFunctionFilters = []
      },
      selectAllDashboards () {
        this.selectedDashboardFilters = [...this.availableDashboardReferencers,]
      },
      deselectAllDashboards () {
        this.selectedDashboardFilters = []
      },
      selectAllPdfReports () {
        this.selectedPdfReportFilters = [...this.availablePdfReportReferencers,]
      },
      deselectAllPdfReports () {
        this.selectedPdfReportFilters = []
      },
      formatFunctionName (fn) {
        if (fn === 'kpi') return 'KPI'
        return fn.charAt(0).toUpperCase() + fn.slice(1)
      },
      formatReferencerName (name, categoryLabel) {
        return name === USAGE_NONE_VALUE ? `Not Used in ${categoryLabel}` : name
      },
    },
  }
</script>

<style lang="scss" scoped>
.company-object-ref-select {
  display: flex;
  flex-direction: row;
  gap: 4px;
}

// Trigger
.select-trigger-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid $grayscale-6;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  min-height: 42px;

  &:hover { border-color: $grayscale-5; }
  &.is-disabled { opacity: 0.6; cursor: not-allowed; }
}

.selected-label {
  flex: 1;
  color: $grayscale-1;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dropdown-icon { color: $grayscale-3; font-size: 20px; margin-left: 8px; }

.action-button {
  height: 42px !important;
  padding: 0 10px !important;
  .material-icons { font-size: 16px; }
}

// Modal overlay
.definition-select-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 40;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
}

.definition-select {
  display: flex;
  flex-direction: column;
  width: 620px;
  max-width: 100vw;
  height: 800px;
  max-height: calc(100vh - 64px);
  background: white;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(0, 0, 0, 0.08);
}

// Header
.definition-select-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.header-title {
  font-size: 15px;
  font-weight: 600;
  color: $grayscale-1;
}

.close-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  &:hover { background: rgba(0, 0, 0, 0.05); }
  .material-icons { font-size: 20px; color: $grayscale-3; }
}

// Search bar
.search-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.search-icon { font-size: 20px; color: $grayscale-4; flex-shrink: 0; }

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  line-height: 20px;
  background: transparent;
  color: $grayscale-1;
  min-width: 0;
  &::placeholder { color: $grayscale-4; }
}

.search-bar-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

// Icon buttons
.icon-button-wrapper { position: relative; }

.icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 6px;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover:not(:disabled) { opacity: 0.75; }
  &.is-disabled {
    opacity: 0.35;
    cursor: default;
    background: #f2f3f4;
    .material-icons { color: #999; }
  }
  .material-icons { font-size: 18px; }

  &--green {
    background: #f0fce8;
    .material-icons { color: #3d7c0a; }
  }

  &--violet {
    background: #faf0ff;
    .material-icons { color: #8b14a1; }
    &.is-active { border-color: #8b14a1; }
  }

  &--orange {
    background: #fff3ec;
    .material-icons { color: #c4550a; }
  }
}

// Custom tooltips
.has-tooltip {
  position: relative;

  &[data-tooltip]::after {
    content: attr(data-tooltip);
    position: absolute;
    top: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    padding: 4px 8px;
    background: rgba(0, 0, 0, 0.8);
    color: white;
    font-size: 12px;
    line-height: 16px;
    border-radius: 4px;
    white-space: nowrap;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s;
    z-index: 50;
  }

  &[data-tooltip]:hover::after {
    opacity: 1;
  }

  &[data-tooltip=""]::after,
  &:not([data-tooltip])::after {
    display: none;
  }

  &--right[data-tooltip]::after {
    left: auto;
    right: 0;
    transform: none;
  }
}

// View options dropdown
.view-options-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: 220px;
  max-height: 60vh;
  overflow-y: auto;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  z-index: 10;
}

.vo-section {
  &:not(:last-child) { border-bottom: 1px solid rgba(0, 0, 0, 0.06); }
}

.vo-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  cursor: pointer;
  &:hover { background: rgba(0, 0, 0, 0.02); }
}

.vo-section-icon { font-size: 18px; color: $grayscale-3; }
.vo-section-title { flex: 1; font-size: 13px; font-weight: 500; color: $grayscale-1; }
.vo-chevron { font-size: 18px; color: $grayscale-4; }

.vo-section-body { padding: 0 14px 10px; }

.vo-option {
  padding: 5px 8px;
  font-size: 13px;
  color: $grayscale-2;
  border-radius: 4px;
  cursor: pointer;
  &:hover { background: rgba(0, 0, 0, 0.03); }
  &.is-active { font-weight: 600; color: $grayscale-1; }
}

// Body
.definition-select-body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

// Items list
.items-list {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
  min-width: 0;
  padding: 4px 0;
  &.is-loading { opacity: 0.5; pointer-events: none; }
}

.group-header {
  display: flex;
  align-items: center;
  padding: 10px 20px 6px;
  cursor: pointer;
  user-select: none;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  margin: 4px 0 0;
}

.group-header-label {
  flex: 1;
  font-size: 14px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.45);
}

.group-chevron { font-size: 18px; color: rgba(0, 0, 0, 0.3); }

// Item rows
.item-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px;
  cursor: pointer;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  border-left: 3px solid transparent;
  transition: background-color 0.1s;

  &:hover { background: rgba(0, 0, 0, 0.02); }

  &.is-selected {
    background: #faf5ff;
    border-left-color: #8b14a1;

    .item-name { font-weight: 600; }
  }
}

.default-option-row {
  border-bottom: 2px solid rgba(0, 0, 0, 0.08);
}

.item-type-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  min-width: 28px;
  border-radius: 4px;
  .material-icons { font-size: 16px; }

  &.icon--green {
    background: #f0fce8;
    .material-icons { color: #3d7c0a; }
  }

  &.icon--sky {
    background: #eef8ff;
    .material-icons { color: #0a6b7c; }
  }

  &.icon--orange {
    background: #fff3ec;
    .material-icons { color: #c4550a; }
  }

  &.icon--red {
    background: #fff0ee;
    .material-icons { color: #b82500; }
  }

  &.icon--gray {
    background: #f2f3f4;
    .material-icons { color: #5a5a5f; }
  }

}

.item-name {
  flex: 1;
  font-size: 16px;
  color: $grayscale-1;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.item-archived-tag {
  display: flex;
  align-items: center;
  .material-icons { font-size: 16px; color: rgba(0, 0, 0, 0.25); }
}

.no-results {
  padding: 32px 20px;
  text-align: center;
  color: $grayscale-4;
  font-size: 14px;
}

// Filter panel
.filter-panel {
  width: 220px;
  min-width: 220px;
  max-width: 220px;
  border-left: 1px solid rgba(0, 0, 0, 0.06);
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

.fp-section {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  &.is-loading { opacity: 0.5; pointer-events: none; }
}

.fp-section-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 14px;
  cursor: pointer;
  &:hover { background: rgba(0, 0, 0, 0.02); }
}

.fp-label {
  flex: 1;
  font-size: 14px;
  font-weight: 500;
  color: $grayscale-2;
  &.is-filtered { color: #8b14a1; }
}

.fp-badge {
  font-size: 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.4);
  &.is-filtered { color: #8b14a1; }
}

.fp-chevron { font-size: 18px; color: $grayscale-4; }

.fp-section-body { padding: 0 14px 10px; }

.fp-search-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 0 8px;
}

.fp-search-icon { font-size: 16px; color: $grayscale-4; }

.fp-search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  background: transparent;
  color: $grayscale-1;
  &::placeholder { color: $grayscale-5; }
}

.fp-options {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.fp-option {
  font-size: 13px;
  color: $grayscale-1;
  max-width: 100%;
  overflow: hidden;

  ::v-deep .b-checkbox {
    min-width: 0;
    max-width: 100%;
    .control-label {
      padding: 8px 0 8px 8px;
      font-size: 16px;
      line-height: 20px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

// Legacy
.select-input { width: 100%; }

.open-selected {
  height: 45px !important;
  margin-left: 4px;
  .material-icons { font-size: 15px; }
}

.refresh-button-legacy {
  border: none;
  font-size: 9px;
  box-shadow: none;
  width: 100%;
  .material-icons { font-size: 14px; }
  span { font-size: 13px; }
}
</style>

